import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { normalizeHttpError } from "../../../lib/http/errors";
import type {
  ItineraryResourceEntryReason,
  ResolvedItineraryResourceEntry,
} from "../../../shared/itinerary-resource-entry";
import {
  createBudgetVersionFromItinerary,
  fetchBudgetEditor,
  fetchRebuildPriceReusePreview,
  fetchBudgetTransportPoints,
  finalizeBudget,
  regenerateV1FromItinerary,
  saveBudgetDraft,
  unfinalizeBudget,
} from "../repo/quoteBudgetRepo";
import {
  calculateBudgetSummary,
  calculateCostBreakdown,
  calculateLineAmount,
  deriveQuantityFromPeople,
  isBusinessTaxLine,
  parseBusFuelFormula,
  withBusinessTaxLine,
} from "../service/quoteBudgetCalculationService";
import {
  applyInlineFormula,
  inlineFormulaValidationMessage,
  isCustomerQuoteLockError,
  isRosterPricingLockError,
  isItineraryChangedFinalizeError,
} from "../service/quoteBudgetEditorViewService";
import { adaptResolvedEntriesToQuoteBudgetLines } from "../service/quoteBudgetQuickEntryAdapter";
import { workspaceCategory, type WorkspaceCategory } from "../service/quoteBudgetWorkspaceService";
import { completeLegacyBusSources } from "../service/quoteBudgetLegacyBusReplacement";
import type {
  QuoteBudgetEditorData,
  QuoteBudgetLine,
  CopyVersionResult,
  SaveDraftLineInput,
  SaveDraftRequest,
} from "../types";

/** 编辑器可改的人数与付费口径草稿. */
export type QuoteBudgetEditorDraft = {
  studentCount: number;
  teacherCount: number;
  guideCount: number;
  driverCount: number;
  teacherPays: boolean;
};

/** 新预算版本已创建、但目标编辑器预加载失败的可恢复错误。 */
export class RebuildVersionPreloadError extends Error {
  readonly code = "QUOTE_BUDGET_REBUILD_PRELOAD_FAILED";

  readonly createdVersion: CopyVersionResult;

  constructor(createdVersion: CopyVersionResult, cause: unknown) {
    const causeMessage = cause instanceof Error ? cause.message : String(cause);
    super(`新版本 V${createdVersion.versionNo} 已生成，但加载失败：${causeMessage}`, { cause });
    this.name = "RebuildVersionPreloadError";
    this.createdVersion = createdVersion;
  }
}

/** 把交通点查询异常转换为保留统一 HTTP 元数据的结构化原因。 */
export function buildTransportPointSearchReason(
  error: unknown,
  field: "fromPoint" | "toPoint" | "transportPoint",
  value: string,
  lineNumber?: number,
): ItineraryResourceEntryReason {
  const normalized = normalizeHttpError(error);
  return {
    code: "transport_point_search_failed",
    field,
    value,
    message: normalized.message,
    ...(lineNumber ? { lineNumber } : {}),
    ...(normalized.code ? { causeCode: normalized.code } : {}),
    ...(normalized.statusCode === null ? {} : { status: normalized.statusCode }),
    ...(normalized.request.traceId ? { traceId: normalized.request.traceId } : {}),
  };
}

/** 从编辑器数据初始化人数草稿. */
function draftFromEditor(editor: QuoteBudgetEditorData): QuoteBudgetEditorDraft {
  const order = editor.budgetOrder;
  return {
    studentCount: order.studentCount,
    teacherCount: order.teacherCount,
    guideCount: order.guideCount,
    driverCount: order.driverCount,
    teacherPays: order.teacherPays,
  };
}

/** 仅比较用户可编辑的人数与分摊口径，恢复服务端原值后不再误报未保存。 */
function draftEquals(
  left: QuoteBudgetEditorDraft | null,
  right: QuoteBudgetEditorDraft | null,
): boolean {
  if (!left || !right) return left === right;
  return left.studentCount === right.studentCount
    && left.teacherCount === right.teacherCount
    && left.guideCount === right.guideCount
    && left.driverCount === right.driverCount
    && left.teacherPays === right.teacherPays;
}

/** 规范十进制文本但不经过浮点转换，保留大数的分位差异。 */
function canonicalDecimalText(value: string | number): string {
  const matched = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!matched) return String(value).trim();
  const integer = matched[2].replace(/^0+(?=\d)/, "");
  const fraction = (matched[3] ?? "").replace(/0+$/, "");
  const isZero = integer === "0" && fraction === "";
  return `${matched[1] === "-" && !isZero ? "-" : ""}${integer}${fraction ? `.${fraction}` : ""}`;
}

/** 比较公式会改变的业务字段，忽略仅因解析器创建新对象产生的引用差异。 */
/** 比较公式派生值字段，不比较价格来源状态。 */
function inlineFormulaValueFieldsEqual(left: QuoteBudgetLine, right: QuoteBudgetLine): boolean {
  const numericEqual = (a: string | number | null | undefined, b: string | number | null | undefined) => {
    if (a == null || b == null) return a == null && b == null;
    return canonicalDecimalText(a) === canonicalDecimalText(b);
  };
  return numericEqual(left.quantity, right.quantity)
    && numericEqual(left.nightCount, right.nightCount)
    && numericEqual(left.dayCount, right.dayCount)
    && numericEqual(left.budgetUnitPrice, right.budgetUnitPrice)
    && numericEqual(left.budgetAmount, right.budgetAmount)
    && (left.priceBasisDesc ?? "") === (right.priceBasisDesc ?? "")
    && JSON.stringify(left.entrySnapshot ?? null) === JSON.stringify(right.entrySnapshot ?? null);
}

/** 比较公式值和价格来源，供脏态、手工锁及保存响应等价判断。 */
function inlineFormulaFieldsEqual(left: QuoteBudgetLine, right: QuoteBudgetLine): boolean {
  return inlineFormulaValueFieldsEqual(left, right)
    && (left.priceSourceType ?? "") === (right.priceSourceType ?? "")
    && (left.priceSourceRef ?? null) === (right.priceSourceRef ?? null);
}

/** 预算行转保存入参，剔除由后端重算的金额字段. */
function toDraftLine(line: QuoteBudgetLine): SaveDraftLineInput {
  const rest: Record<string, unknown> = { ...line };
  delete rest.budgetAmount;
  delete rest.budgetOrderId;
  if (
    typeof rest.budgetLineId === "string"
    && (rest.budgetLineId.startsWith("tmp:") || rest.budgetLineId.startsWith("system:"))
  ) {
    delete rest.budgetLineId;
  }
  return rest as SaveDraftLineInput;
}

const DRAFT_LINE_NUMERIC_FIELDS = [
  "quantity",
  "nightCount",
  "dayCount",
  "budgetUnitPrice",
] as const;

/** 把预算行转换为稳定的草稿比较值，忽略后端派生金额与纯展示行号。 */
function comparableDraftLine(line: QuoteBudgetLine): string {
  const value = { ...toDraftLine(line) } as Record<string, unknown>;
  delete value.lineNo;
  for (const field of DRAFT_LINE_NUMERIC_FIELDS) {
    const raw = value[field];
    if (raw === null || raw === undefined || raw === "") continue;
    value[field] = canonicalDecimalText(raw as string | number);
  }
  return JSON.stringify(
    Object.fromEntries(Object.keys(value).sort().map((key) => [key, value[key]])),
  );
}

/** 比较完整可保存明细；数量格式等价时不误报，新增、删除、身份或价格变化均可识别。 */
function draftLinesEqual(left: QuoteBudgetLine[], right: QuoteBudgetLine[]): boolean {
  return left.length === right.length
    && left.every((line, index) => comparableDraftLine(line) === comparableDraftLine(right[index]));
}

/** 编辑器查询键. */
function editorQueryKey(budgetOrderId: string) {
  return ["quoteBudgetEditor", budgetOrderId] as const;
}

/** 异步动作完成时编辑器已经切换预算或请求代次。 */
class EditorContextChangedError extends Error {
  constructor() {
    super("编辑器上下文已切换，已忽略旧请求结果。");
    this.name = "EditorContextChangedError";
  }
}

/** 路由代次查询键：隔离离开页面后仍未决的同 ID 请求。 */
function editorGenerationQueryKey(budgetOrderId: string, generation: number) {
  return [...editorQueryKey(budgetOrderId), generation] as const;
}

type EditorRequestContext = {
  budgetOrderId: string;
  generation: number;
};

type SaveMutationInput = {
  context: EditorRequestContext;
  request: SaveDraftRequest;
  manualLines: QuoteBudgetLine[];
};

/** 比较新增行在保存前后可稳定保留的业务身份字段。 */
function manualLineIdentityEqual(left: QuoteBudgetLine, right: QuoteBudgetLine): boolean {
  return left.lineNo === right.lineNo
    && left.resourceType === right.resourceType
    && left.resourceEntryType === right.resourceEntryType
    && left.itemNameSnapshot === right.itemNameSnapshot
    && left.itemSpecSnapshot === right.itemSpecSnapshot
    && left.travelerRole === right.travelerRole
    && left.unit === right.unit;
}

/** 只保留权威保存响应中仍与本次人工公式结果等价的行锁。 */
function equivalentManualLineIds(
  authoritativeLines: QuoteBudgetLine[],
  manualLines: QuoteBudgetLine[],
): Set<string> {
  const manualById = new Map(manualLines.map((line) => [line.budgetLineId, line]));
  const matchedManualLineIds = new Set<string>();
  const lockedLineIds = new Set<string>();
  for (const line of authoritativeLines) {
    if (line.priceSourceType !== "manual") continue;
    const byId = manualById.get(line.budgetLineId);
    let candidate = byId && !matchedManualLineIds.has(byId.budgetLineId)
      ? byId
      : undefined;
    if (!candidate) {
      const matches = manualLines.filter((item) => (
        !matchedManualLineIds.has(item.budgetLineId)
        && manualLineIdentityEqual(line, item)
        && inlineFormulaFieldsEqual(line, item)
      ));
      candidate = matches.length === 1 ? matches[0] : undefined;
    }
    if (
      candidate?.priceSourceType !== "manual"
      || !inlineFormulaFieldsEqual(line, candidate)
    ) continue;
    matchedManualLineIds.add(candidate.budgetLineId);
    lockedLineIds.add(line.budgetLineId);
  }
  return lockedLineIds;
}

/** 仅识别核价草稿的乐观并发冲突，其他 409 继续走各自恢复流程。 */
function isConcurrentUpdateError(error: unknown): boolean {
  const normalized = normalizeHttpError(error);
  return normalized.statusCode === 409 && normalized.code === "QUOTE_BUDGET_CONCURRENT_UPDATE";
}

type TrustedEditorPreload = {
  editor: QuoteBudgetEditorData;
  source: EditorRequestContext;
};

const trustedEditorPreloads = new WeakMap<QueryClient, Map<string, TrustedEditorPreload>>();
const editorGenerationCounters = new WeakMap<QueryClient, number>();

/** 在同一 QueryClient 内为每次预算路由激活分配唯一代次，覆盖按 key 重挂载场景。 */
function createEditorGeneration(queryClient: QueryClient): number {
  const generation = (editorGenerationCounters.get(queryClient) ?? 0) + 1;
  editorGenerationCounters.set(queryClient, generation);
  return generation;
}

/** 记录当前切换流程刚完成的目标快照；普通查询缓存不具备跨路由直显资格。 */
function rememberTrustedEditorPreload(
  queryClient: QueryClient,
  targetBudgetOrderId: string,
  preload: TrustedEditorPreload,
) {
  const clientPreloads = trustedEditorPreloads.get(queryClient) ?? new Map();
  clientPreloads.set(targetBudgetOrderId, preload);
  trustedEditorPreloads.set(queryClient, clientPreloads);
}

/** 任一路由激活时撤销其他目标的切换凭证，避免绕行后误信旧预加载。 */
function discardUnrelatedTrustedEditorPreloads(
  queryClient: QueryClient,
  currentBudgetOrderId: string,
) {
  const clientPreloads = trustedEditorPreloads.get(queryClient);
  if (!clientPreloads) return;
  for (const targetBudgetOrderId of clientPreloads.keys()) {
    if (targetBudgetOrderId !== currentBudgetOrderId) {
      clientPreloads.delete(targetBudgetOrderId);
    }
  }
  if (clientPreloads.size === 0) trustedEditorPreloads.delete(queryClient);
}

/** 在渲染阶段读取可信快照，兼容 StrictMode 的重复渲染。 */
function peekTrustedEditorPreload(
  queryClient: QueryClient,
  targetBudgetOrderId: string,
): TrustedEditorPreload | null {
  return trustedEditorPreloads.get(queryClient)?.get(targetBudgetOrderId) ?? null;
}

/** 目标路由提交后按同一 DTO 确认消费，避免误删更新的预加载。 */
function consumeTrustedEditorPreload(
  queryClient: QueryClient,
  targetBudgetOrderId: string,
  expectedEditor: QuoteBudgetEditorData,
) {
  const clientPreloads = trustedEditorPreloads.get(queryClient);
  if (clientPreloads?.get(targetBudgetOrderId)?.editor !== expectedEditor) return;
  clientPreloads.delete(targetBudgetOrderId);
  if (clientPreloads?.size === 0) trustedEditorPreloads.delete(queryClient);
}

type FinalizeMutationInput = {
  context: EditorRequestContext;
  confirmRiskCodes: string[];
  versionName?: string;
};

/** 为一次重建意图生成跨网络重试稳定的请求键。 */
function createRebuildIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  const values = new Uint32Array(4);
  globalThis.crypto?.getRandomValues(values);
  return `rebuild-${Date.now()}-${Array.from(values, (value) => value.toString(16)).join("")}`;
}

/** 生成当前页面实际展示/保存的预算行，旧固定费拆行会先按大巴核价段归并。 */
function buildEffectiveLines(
  lines: QuoteBudgetLine[],
  draft: QuoteBudgetEditorDraft | null,
  manualLineIds: Set<string>,
  calculateTax: boolean,
): QuoteBudgetLine[] {
  const normalizedLines = lines;
  if (!draft) {
    return normalizedLines;
  }
  const linkedLines = normalizedLines.map((line) => {
    if (manualLineIds.has(line.budgetLineId)) {
      return line;
    }
    if (!line.isLinkedToPeopleCount || (!line.entrySnapshot && line.resourceType === "包车")) {
      return line;
    }
    const quantity = String(deriveQuantityFromPeople(line, draft));
    if (quantity === String(line.quantity)) {
      return line;
    }
    const next: QuoteBudgetLine = { ...line, quantity };
    next.budgetAmount = calculateLineAmount(next);
    return next;
  });
  if (!calculateTax) return linkedLines;
  return withBusinessTaxLine(linkedLines);
}

/** 识别需要由当前可编辑草稿显式保存迁移的旧开票税金行。 */
function hasLegacyInvoiceTaxMarkers(lines: QuoteBudgetLine[]): boolean {
  return lines.some((line) => {
    if (String(line.itemNameSnapshot ?? "").trim() !== "开票税金") return false;
    return line.resourceType !== "杂费"
      || String(line.resourceEntryType ?? "").trim() !== "manual"
      || String(line.calcType ?? "").trim() !== "snapshot"
      || line.isAgentCollection === true
      || line.isLocked === true
      || String(line.priceSourceType ?? "").trim() !== "manual"
      || line.priceSourceRef != null;
  });
}

const MAX_SAVE_DRAFT_LINE_NO = 2_147_483_646;

/** 为整批新增普通行分配合法行号，并为随草稿提交的系统营业税预留一个空闲号。 */
function allocateNewOrdinaryLineNos(lines: QuoteBudgetLine[], count: number): number[] {
  if (count <= 0) return [];
  const ordinaryLines = lines.filter((line) => !isBusinessTaxLine(line));
  const used = new Set(ordinaryLines.map((line) => line.lineNo));
  const maximum = ordinaryLines.reduce(
    (value, line) => Math.max(value, line.lineNo),
    0,
  );
  if (
    maximum + count <= MAX_SAVE_DRAFT_LINE_NO
    && used.size + count < MAX_SAVE_DRAFT_LINE_NO
  ) {
    return Array.from({ length: count }, (_, index) => maximum + index + 1);
  }

  const available: number[] = [];
  for (
    let candidate = 1;
    candidate <= MAX_SAVE_DRAFT_LINE_NO && available.length < count + 1;
    candidate += 1
  ) {
    if (!used.has(candidate)) available.push(candidate);
  }
  if (available.length <= count) {
    throw new Error("核价明细行号已用尽，无法继续加入明细");
  }
  return available.slice(0, count);
}

/**
 * 编辑器运行时：拉取数据、维护人数草稿与脏标记、保存并刷新.
 * @param budgetOrderId 预算单标识
 * @param canMutate 当前是否拥有统一核价操作权限；默认拒绝。
 */
export function useQuoteBudgetEditorRuntime(budgetOrderId: string, canMutate = false) {
  const queryClient = useQueryClient();
  const canMutateRef = useRef(canMutate);
  const mountedRef = useRef(true);
  const preloadAttemptRef = useRef(0);
  const rebuiltVersionRef = useRef<Awaited<ReturnType<typeof createBudgetVersionFromItinerary>> | null>(null);
  const rebuildSourceReadonlyRef = useRef(false);
  const priceReusePreviewContextRef = useRef<EditorRequestContext | null>(null);
  const rebuildRequestRef = useRef<{
    context: EditorRequestContext;
    idempotencyKey: string;
    reuseExactMatchedPrices: boolean;
  } | null>(null);
  const authoritativeManualLocksRef = useRef<{
    context: EditorRequestContext;
    revision: string;
  } | null>(null);
  canMutateRef.current = canMutate;
  const contextRef = useRef<EditorRequestContext | null>(null);
  if (contextRef.current?.budgetOrderId !== budgetOrderId) {
    const hadPreviousContext = contextRef.current !== null;
    contextRef.current = {
      budgetOrderId,
      generation: createEditorGeneration(queryClient),
    };
    if (hadPreviousContext) preloadAttemptRef.current += 1;
    rebuiltVersionRef.current = null;
    rebuildSourceReadonlyRef.current = false;
    rebuildRequestRef.current = null;
    priceReusePreviewContextRef.current = null;
    authoritativeManualLocksRef.current = null;
  }
  const contextGeneration = contextRef.current!.generation;
  const matchesActiveContext = (requestContext: EditorRequestContext) => {
    const current = contextRef.current;
    return current !== null
      && current.budgetOrderId === requestContext.budgetOrderId
      && current.generation === requestContext.generation;
  };
  const observedRouteRef = useRef<EditorRequestContext | null>(null);
  const trustedEditorRef = useRef<{
    context: EditorRequestContext;
    editor: QuoteBudgetEditorData;
  } | null>(null);
  if (
    observedRouteRef.current?.budgetOrderId !== budgetOrderId
    || observedRouteRef.current.generation !== contextGeneration
  ) {
    observedRouteRef.current = { budgetOrderId, generation: contextGeneration };
    discardUnrelatedTrustedEditorPreloads(queryClient, budgetOrderId);
    const preload = peekTrustedEditorPreload(queryClient, budgetOrderId);
    trustedEditorRef.current =
      preload?.editor.budgetOrder.budgetOrderId === budgetOrderId
        ? {
            context: { budgetOrderId, generation: contextGeneration },
            editor: preload.editor,
          }
        : null;
  }
  const trustedEditor =
    trustedEditorRef.current?.context.budgetOrderId === budgetOrderId
    && trustedEditorRef.current.context.generation === contextGeneration
      ? trustedEditorRef.current.editor
      : null;
  const [acceptedEditorGeneration, setAcceptedEditorGeneration] = useState<number | null>(null);
  const query = useQuery({
    queryKey: editorGenerationQueryKey(budgetOrderId, contextGeneration),
    queryFn: () => fetchBudgetEditor(budgetOrderId),
    enabled: Boolean(budgetOrderId),
    placeholderData: keepPreviousData,
    retry: false,
  });
  useEffect(() => {
    setAcceptedEditorGeneration(null);
  }, [budgetOrderId, contextGeneration]);
  useEffect(() => {
    if (
      query.data
      && !query.isFetching
      && !query.isPlaceholderData
      && query.data.budgetOrder.budgetOrderId === budgetOrderId
    ) {
      setAcceptedEditorGeneration(contextGeneration);
    }
  }, [budgetOrderId, contextGeneration, query.data, query.isFetching, query.isPlaceholderData]);
  useEffect(() => {
    if (trustedEditor) {
      consumeTrustedEditorPreload(queryClient, budgetOrderId, trustedEditor);
    }
  }, [budgetOrderId, contextGeneration, queryClient, trustedEditor]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      preloadAttemptRef.current += 1;
    };
  }, []);
  const queryEditorReady = acceptedEditorGeneration === contextGeneration;
  const editorGenerationReady = queryEditorReady || trustedEditor !== null;
  const currentEditor =
    queryEditorReady
    && query.data
    && !query.isPlaceholderData
    && query.data.budgetOrder.budgetOrderId === budgetOrderId
      ? query.data
      : trustedEditor;
  const canCalculateTax = Boolean(
    canMutate
    && currentEditor
    && !currentEditor.readonly
    && currentEditor.budgetOrder.status === "draft",
  );

  /** 版本切换前把目标编辑器写入查询缓存，避免路由更新后回退到骨架屏。 */
  const preloadVersion = useCallback(
    async (targetBudgetOrderId: string) => {
      const source = { ...contextRef.current! };
      const attempt = ++preloadAttemptRef.current;
      trustedEditorPreloads.delete(queryClient);
      const editor = await fetchBudgetEditor(targetBudgetOrderId);
      const current = contextRef.current;
      const contextStillActive = (
        mountedRef.current
        && preloadAttemptRef.current === attempt
        && current !== null
        && current.budgetOrderId === source.budgetOrderId
        && current.generation === source.generation
        && editor.budgetOrder.budgetOrderId === targetBudgetOrderId
      );
      if (!contextStillActive) throw new EditorContextChangedError();
      queryClient.setQueryData(editorQueryKey(targetBudgetOrderId), editor);
      rememberTrustedEditorPreload(queryClient, targetBudgetOrderId, { editor, source });
      return editor;
    },
    [queryClient],
  );

  /** 搜索大交通点位，复用 React Query 缓存与统一 repository。 */
  const fetchTransportPointCandidates = useCallback(
    (mode: "air" | "rail", q: string) =>
      queryClient.fetchQuery({
        queryKey: ["quoteBudgetTransportPoints", budgetOrderId, mode, q],
        queryFn: () => fetchBudgetTransportPoints(budgetOrderId, mode, q),
      }),
    [budgetOrderId, queryClient],
  );

  /** 为共享 resolver 适配核价交通点字段，并把 HTTP 异常预归一后交给共享层补齐端点信息。 */
  const searchTransportPoints = useCallback(
    async (mode: "air" | "rail", q: string) => {
      try {
        const points = await fetchTransportPointCandidates(mode, q);
        return points.map((point) => ({
          id: point.pointId,
          name: point.name,
          shortName: point.shortName,
          pointType: point.pointType,
          city: point.city,
        }));
      } catch (error) {
        const normalized = normalizeHttpError(error);
        throw {
          code: "transport_point_search_failed",
          field: "transportPoint",
          value: q,
          message: normalized.message,
          ...(normalized.code ? { causeCode: normalized.code } : {}),
          ...(normalized.statusCode === null ? {} : { status: normalized.statusCode }),
          ...(normalized.request.traceId ? { traceId: normalized.request.traceId } : {}),
        };
      }
    },
    [fetchTransportPointCandidates],
  );

  const [draft, setDraft] = useState<QuoteBudgetEditorDraft | null>(() =>
    currentEditor ? draftFromEditor(currentEditor) : null,
  );
  const [baselineDraft, setBaselineDraft] = useState<QuoteBudgetEditorDraft | null>(() =>
    currentEditor ? draftFromEditor(currentEditor) : null,
  );
  const [baselineRevision, setBaselineRevision] = useState<string | null>(
    () => currentEditor?.revision ?? null,
  );
  // 工作态预算行：从服务端回填，行内公式/新增/删除在此就地维护，保存草稿统一落库.
  const [lines, setLines] = useState<QuoteBudgetLine[]>(() => currentEditor?.lines ?? []);
  const [baselineLines, setBaselineLines] = useState<QuoteBudgetLine[]>(() =>
    currentEditor?.lines ?? [],
  );
  const hasLineChanges = !draftLinesEqual(lines, baselineLines);
  const calculateTax = canCalculateTax && (hasLineChanges || !draftEquals(draft, baselineDraft));
  const hasPendingInvoiceTaxMigration = canCalculateTax && hasLegacyInvoiceTaxMarkers(lines);
  const isDirty = hasLineChanges
    || hasPendingInvoiceTaxMigration
    || !draftEquals(draft, baselineDraft);
  // 行内公式手改过的普通行不再随人数重算；大巴不按人数重新排车或刷新成本。
  const [manualLineIds, setManualLineIds] = useState<Set<string>>(() => new Set());
  // 最近一次加入明细的行 id：驱动明细面板「新加入」高亮与置顶（QB-PU-013/021）.
  const [recentLineIds, setRecentLineIds] = useState<string[]>([]);
  const [deletedLinesByCategory, setDeletedLinesByCategory] = useState<Partial<Record<WorkspaceCategory, QuoteBudgetLine[]>>>({});
  const deletedLinesByCategoryRef = useRef(deletedLinesByCategory);
  const replaceDeletedLines = useCallback((next: Partial<Record<WorkspaceCategory, QuoteBudgetLine[]>>) => {
    deletedLinesByCategoryRef.current = next;
    setDeletedLinesByCategory(next);
  }, []);
  const recordDeletedLines = useCallback((removed: QuoteBudgetLine[]) => {
    if (!removed.length) return;
    const grouped: Partial<Record<WorkspaceCategory, QuoteBudgetLine[]>> = {};
    for (const line of removed) {
      const category = workspaceCategory(line);
      (grouped[category] ??= []).push(line);
    }
    replaceDeletedLines({ ...deletedLinesByCategoryRef.current, ...grouped });
  }, [replaceDeletedLines]);
  const localMutationAllowedRef = useRef(false);
  const [lockRefreshFailure, setLockRefreshFailure] = useState<{
    context: EditorRequestContext;
    error: Error;
  } | null>(null);
  const [saveConflictContext, setSaveConflictContext] = useState<EditorRequestContext | null>(null);
  const [conflictReloadFailure, setConflictReloadFailure] = useState<{
    context: EditorRequestContext;
    error: Error;
  } | null>(null);
  const workingContextRef = useRef<EditorRequestContext>({
    budgetOrderId,
    generation: contextGeneration,
  });
  if (
    workingContextRef.current.budgetOrderId !== budgetOrderId
    || workingContextRef.current.generation !== contextGeneration
  ) {
    workingContextRef.current = { budgetOrderId, generation: contextGeneration };
    const nextDraft = currentEditor ? draftFromEditor(currentEditor) : null;
    setDraft(nextDraft);
    setBaselineDraft(nextDraft);
    setBaselineRevision(currentEditor?.revision ?? null);
    setLines(currentEditor?.lines ?? []);
    setBaselineLines(currentEditor?.lines ?? []);
    setManualLineIds(new Set());
    setRecentLineIds([]);
    replaceDeletedLines({});
    setLockRefreshFailure(null);
    setSaveConflictContext(null);
    setConflictReloadFailure(null);
  }

  /** 用服务端权威响应原子替换查询缓存、工作态和保存基线。 */
  const applyAuthoritativeEditor = useCallback((
    editor: QuoteBudgetEditorData,
    requestContext: EditorRequestContext,
    nextManualLineIds = new Set<string>(),
  ) => {
    const current = contextRef.current;
    if (
      current === null
      || current.budgetOrderId !== requestContext.budgetOrderId
      || current.generation !== requestContext.generation
    ) return false;
    queryClient.setQueryData(
      editorGenerationQueryKey(requestContext.budgetOrderId, requestContext.generation),
      editor,
    );
    authoritativeManualLocksRef.current = nextManualLineIds.size > 0
      ? { context: requestContext, revision: editor.revision }
      : null;
    const nextDraft = draftFromEditor(editor);
    setDraft(nextDraft);
    setBaselineDraft(nextDraft);
    setBaselineRevision(editor.revision);
    setLines(editor.lines);
    setBaselineLines(editor.lines);
    setManualLineIds(nextManualLineIds);
    setRecentLineIds([]);
    replaceDeletedLines({});
    return true;
  }, [queryClient, replaceDeletedLines]);

  /** 锁定冲突后发起独立强刷；仅当前请求上下文的权威锁定快照可以丢弃本地编辑。 */
  const refreshAfterCustomerQuoteLock = useCallback(async (
    error: unknown,
    requestContext: EditorRequestContext,
  ) => {
    if (!isCustomerQuoteLockError(error)) return;
    const isCurrentContext = () =>
      contextRef.current?.budgetOrderId === requestContext.budgetOrderId
      && contextRef.current?.generation === requestContext.generation;
    if (!isCurrentContext()) return;
    setLockRefreshFailure(null);
    try {
      await queryClient.cancelQueries({
        queryKey: editorGenerationQueryKey(
          requestContext.budgetOrderId,
          requestContext.generation,
        ),
        exact: true,
      });
      if (!isCurrentContext()) return;
      const lockedEditor = await fetchBudgetEditor(requestContext.budgetOrderId);
      if (!isCurrentContext()) return;
      if (
        lockedEditor.budgetOrder.budgetOrderId !== requestContext.budgetOrderId
        || (
          lockedEditor.readonlyReason !== "customer_quote_finalized"
          && !lockedEditor.budgetOrder.lockedByCustomerQuoteId
        )
      ) {
        throw new Error("未获取到已定稿报价锁定快照");
      }
      queryClient.setQueryData(
        editorGenerationQueryKey(requestContext.budgetOrderId, requestContext.generation),
        lockedEditor,
      );
      const lockedDraft = draftFromEditor(lockedEditor);
      setDraft(lockedDraft);
      setBaselineDraft(lockedDraft);
      setBaselineRevision(lockedEditor.revision);
      setLines(lockedEditor.lines);
      replaceDeletedLines({});
      setBaselineLines(lockedEditor.lines);
      authoritativeManualLocksRef.current = null;
      setManualLineIds(new Set());
      setRecentLineIds([]);
      setSaveConflictContext(null);
      setConflictReloadFailure(null);
      setLockRefreshFailure(null);
    } catch (refreshError) {
      if (!isCurrentContext()) return;
      setLockRefreshFailure({
        context: requestContext,
        error: refreshError instanceof Error
          ? refreshError
          : new Error("锁定快照刷新失败"),
      });
    }
  }, [queryClient, replaceDeletedLines]);

  // 未编辑时跟随服务端数据回填；脏状态下保留用户输入.
  useEffect(() => {
    if (currentEditor && !isDirty) {
      const authoritativeManualLocks = authoritativeManualLocksRef.current;
      const shouldResetManualLines = !(
        authoritativeManualLocks
        && authoritativeManualLocks.context.budgetOrderId === budgetOrderId
        && authoritativeManualLocks.context.generation === contextGeneration
        && authoritativeManualLocks.revision === currentEditor.revision
      );
      const nextDraft = draftFromEditor(currentEditor);
      setDraft(nextDraft);
      setBaselineDraft(nextDraft);
      setBaselineRevision(currentEditor.revision);
      setLines(currentEditor.lines);
      if (baselineRevision !== currentEditor.revision) replaceDeletedLines({});
      setBaselineLines(currentEditor.lines);
      if (shouldResetManualLines) setManualLineIds(new Set());
    }
  }, [baselineRevision, budgetOrderId, contextGeneration, currentEditor, isDirty, replaceDeletedLines]);

  const patchDraft = useCallback((patch: Partial<QuoteBudgetEditorDraft>) => {
    if (!canMutateRef.current) return;
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  /** 行内公式 Enter：解析文本写回数量/单价并重算金额，置脏待保存（QB-PU-021）. */
  const applyLineFormula = useCallback((lineId: string, formulaText: string) => {
    if (
      !canMutateRef.current
      || !currentEditor
      || currentEditor.readonly
      || currentEditor.budgetOrder.status !== "draft"
    ) return false;
    const currentLines = buildEffectiveLines(lines, draft, manualLineIds, calculateTax);
    const target = currentLines
      .find((line) => line.budgetLineId === lineId);
    if (!target) return false;
    const busFuel = parseBusFuelFormula(formulaText.trim());
    if (
      busFuel
      && (target.resourceType !== "包车" || target.resourceEntryType === "包车固定费")
    ) return false;
    const validationFormula = busFuel
      ? `1车趟 x ${busFuel.amount}元`
      : formulaText;
    if (inlineFormulaValidationMessage(target, validationFormula)) return false;
    let updated = busFuel
      ? {
          ...target,
          quantity: "1",
          budgetUnitPrice: busFuel.amount,
          budgetAmount: busFuel.amount,
          priceSourceType: "manual",
          priceBasisDesc: busFuel.basis,
        }
      : applyInlineFormula(target, formulaText);
    updated = {
      ...updated,
      budgetAmount: calculateLineAmount(updated),
      priceSourceType: "manual",
      priceSourceRef: null,
    };
    // 重复确认已有价格的原值不应产生脏状态；只有尚未核价行确认 0 元时才必须切换为 manual。
    if (
      target.priceSourceType !== "unpriced"
      && inlineFormulaValueFieldsEqual(target, updated)
    ) {
      updated = {
        ...updated,
        priceSourceType: target.priceSourceType,
        priceSourceRef: target.priceSourceRef,
      };
    }
    if (updated === target || inlineFormulaFieldsEqual(target, updated)) return false;
    const baselineLine = baselineLines.find((line) => line.budgetLineId === lineId);
    const restoredToBaseline = Boolean(
      baselineLine
      && baselineLine.priceSourceType !== "unpriced"
      && inlineFormulaValueFieldsEqual(baselineLine, updated),
    );
    if (restoredToBaseline && baselineLine) updated = baselineLine;
    const nextLines = currentLines.map((line) =>
      line.budgetLineId === lineId ? updated : line,
    );
    const storesTaxLine = lines.some(isBusinessTaxLine);
    const sourceUserLineIds = lines
      .filter((line) => !isBusinessTaxLine(line))
      .map((line) => line.budgetLineId);
    const effectiveUserLineIds = currentLines
      .filter((line) => !isBusinessTaxLine(line))
      .map((line) => line.budgetLineId);
    const effectiveUserLineIdSet = new Set(effectiveUserLineIds);
    // 规范化可能只重排明细（例如燃油行前置），不应因顺序变化回退为整数组覆盖。
    const keepsSourceRows = sourceUserLineIds.length === effectiveUserLineIds.length
      && sourceUserLineIds.every((id) => effectiveUserLineIdSet.has(id));
    setLines((previousLines) => {
      const directLineIndex = previousLines.findIndex((line) => line.budgetLineId === lineId);
      if (keepsSourceRows && directLineIndex >= 0) {
        return previousLines.map((line, index) => (index === directLineIndex ? updated : line));
      }
      return storesTaxLine ? nextLines : nextLines.filter((line) => !isBusinessTaxLine(line));
    });
    // 显式数量按持久化人工语义锁定；只调单价的联动行继续随人数变化。
    setManualLineIds((prev) => {
      const next = new Set(prev);
      if (restoredToBaseline || updated.isLinkedToPeopleCount) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
    return true;
  }, [baselineLines, calculateTax, currentEditor, draft, lines, manualLineIds]);

  /** 删除明细行：从工作态移除，置脏待保存（原型 删 按钮）. */
  const removeLine = useCallback((lineId: string) => {
    if (
      !canMutateRef.current
      || !currentEditor
      || currentEditor.readonly
      || currentEditor.budgetOrder.status !== "draft"
    ) return;
    const source = lines.find((line) => line.budgetLineId === lineId);
    const removed = (source?.resourceEntryType === "包车固定费"
      ? buildEffectiveLines(lines, draft, manualLineIds, calculateTax)
      : lines).filter((line) => line.budgetLineId === lineId);
    if (removed.some((line) => line.isLocked)) return;
    recordDeletedLines(removed);
    setLines((prev) => {
      const sourceLine = prev.find((line) => line.budgetLineId === lineId);
      if (sourceLine?.resourceEntryType !== "包车固定费") {
        return prev.filter((line) => line.budgetLineId !== lineId);
      }
      return buildEffectiveLines(prev, draft, manualLineIds, calculateTax)
        .filter((line) => line.budgetLineId !== lineId);
    });
  }, [calculateTax, currentEditor, draft, lines, manualLineIds, recordDeletedLines]);

  /** 删除某一资源类型的全部明细，其他类型保持不变并置脏待保存。 */
  const removeResourceType = useCallback((resourceType: string) => {
    if (
      !canMutateRef.current
      || !currentEditor
      || currentEditor.readonly
      || currentEditor.budgetOrder.status !== "draft"
    ) return;
    recordDeletedLines(lines.filter((line) => line.resourceType === resourceType && !isBusinessTaxLine(line) && !line.isLocked));
    setLines((prev) => prev.filter(
      (line) => line.resourceType !== resourceType || isBusinessTaxLine(line) || line.isLocked,
    ));
    setRecentLineIds([]);
  }, [currentEditor, lines, recordDeletedLines]);

  /** 按当前费用类型补回最近删除的缺失行，不回滚删除后的人数、改价或新增内容。 */
  const undoLastDeletion = useCallback((category: WorkspaceCategory) => {
    if (!localMutationAllowedRef.current
      || workingContextRef.current.budgetOrderId !== budgetOrderId
      || workingContextRef.current.generation !== contextGeneration) return;
    const deletedLines = deletedLinesByCategoryRef.current[category];
    if (!deletedLines?.length) return;
    setLines((current) => {
      const ids = new Set(current.map((line) => line.budgetLineId));
      const usedLineNos = new Set(current.filter((line) => !isBusinessTaxLine(line)).map((line) => line.lineNo));
      const claimed = new Set(current.flatMap(line => line.replacesLineIds ?? []));
      const restored = deletedLines.filter((line) => !ids.has(line.budgetLineId) && !claimed.has(line.budgetLineId)).map((line) => {
        let lineNo = line.lineNo;
        if (usedLineNos.has(lineNo)) {
          lineNo = 1;
          while (usedLineNos.has(lineNo)) lineNo += 1;
        }
        usedLineNos.add(lineNo);
        return { ...line, lineNo };
      });
      return [...current, ...restored];
    });
    const remaining = { ...deletedLinesByCategoryRef.current };
    delete remaining[category];
    replaceDeletedLines(remaining);
  }, [budgetOrderId, contextGeneration, replaceDeletedLines]);

  /** 共享录入面板整批解析成功后的核价原子加入端口。 */
  const addResolvedEntries = useCallback((entries: ResolvedItineraryResourceEntry[]) => {
    if (!canMutateRef.current) {
      throw {
        code: "editor_readonly",
        field: "editor",
        message: "当前账号已无修改行程核价的权限",
      };
    }
    if (
      !currentEditor
      || !draft
      || currentEditor.readonly
      || currentEditor.budgetOrder.status !== "draft"
    ) {
      throw {
        code: currentEditor ? "editor_readonly" : "editor_not_ready",
        field: "editor",
        message: currentEditor ? "当前核价版本不可修改" : "编辑器尚未就绪，无法加入明细",
      };
    }
    const lineTemplates = adaptResolvedEntriesToQuoteBudgetLines(entries, 1);
    setLines((prev) => {
      const lineNos = allocateNewOrdinaryLineNos(prev, lineTemplates.length);
      const newLines = lineTemplates.map((line, index) => ({
        ...line,
        lineNo: lineNos[index],
      }));
      return [...prev, ...newLines];
    });
    setRecentLineIds(lineTemplates.map((line) => line.budgetLineId));
  }, [currentEditor, draft]);

  /** 新版整批确认只替换用户明确选择的行，锁定行和范围失效时整批拒绝。 */
  const commitManualLines = useCallback((newLines: QuoteBudgetLine[], replaceIds: string[] = []) => {
    if (!localMutationAllowedRef.current || workingContextRef.current.budgetOrderId !== budgetOrderId) return false;
    const ids = new Set(replaceIds);
    const current = buildEffectiveLines(lines, draft, manualLineIds, calculateTax);
    if (replaceIds.some((id) => !current.some((line) => line.budgetLineId === id))
      || current.some((line) => ids.has(line.budgetLineId) && (line.isLocked || isBusinessTaxLine(line)))
      || newLines.some((line) => line.isLocked || isBusinessTaxLine(line))) return false;
    const remaining = lines.filter((line) => !ids.has(line.budgetLineId));
    const newIds = new Set(newLines.map((line) => line.budgetLineId));
    if (newIds.size !== newLines.length || remaining.some((line) => newIds.has(line.budgetLineId))) return false;
    const replaced = current.filter((line) => ids.has(line.budgetLineId));
    const legacyBusIds = completeLegacyBusSources(replaced, baselineLines);
    // 仍在主表的旧行不能被静默移除，也不能与替换费用重复计价。
    if (newLines.some(line => line.resourceType === "包车")
      && remaining.some(line => legacyBusIds.includes(line.budgetLineId))) return false;
    const recalculateLegacyBusAmount = replaced.some(line => line.recalculateLegacyBusAmount)
      || legacyBusIds.some(id => !current.some(line => line.budgetLineId === id)
        && !replaced.some(line => line.replacesLineIds?.includes(id)));
    const consumedIds = new Set<string>();
    const retainedById = new Map(replaced.map((line) => [line.budgetLineId, line.lineNo]));
    const usedLineNos = new Set(remaining.map((line) => line.lineNo));
    let nextLineNo = Math.max(0, ...current.map((line) => line.lineNo)) + 1;
    const patched = newLines.map((input) => {
      // 原项目只能由编辑入口携带的明确行 ID 关联，改名、改单位不改变身份。
      const previous = replaced.find((old) => old.budgetLineId === input.budgetLineId);
      let line = input;
      if (input.resourceType === "包车" && legacyBusIds.length) {
        line = { ...line, replacesLineIds: legacyBusIds, recalculateLegacyBusAmount };
        if (new Set(replaced.map(old => Boolean(old.isAgentCollection))).size === 1) {
          line.isAgentCollection = replaced[0]?.isAgentCollection;
        }
        if (new Set(replaced.map(old => old.isCapitaShared)).size === 1) line.isCapitaShared = replaced[0]?.isCapitaShared;
      }
      if (previous && !consumedIds.has(previous.budgetLineId)) {
        consumedIds.add(previous.budgetLineId);
        line = { ...line, replacesLineIds: line.replacesLineIds ?? previous.replacesLineIds, budgetLineId: previous.budgetLineId,
          isAgentCollection: previous.isAgentCollection, isCapitaShared: previous.isCapitaShared,
          entrySnapshot: input.entrySnapshot && previous.entrySnapshot ? {
            ...input.entrySnapshot, groupId: previous.entrySnapshot.groupId,
            conversion: previous.entrySnapshot.conversion,
          } : input.entrySnapshot };
        line = { ...line, budgetAmount: calculateLineAmount(line) };
      }
      let lineNo = retainedById.get(line.budgetLineId)
        ?? replaced.find((old) => !usedLineNos.has(old.lineNo))?.lineNo
        ?? nextLineNo++;
      while (usedLineNos.has(lineNo)) lineNo += 1;
      usedLineNos.add(lineNo);
      return { ...line, lineNo };
    });
    const firstReplaced = current.findIndex((line) => ids.has(line.budgetLineId));
    const insertion = firstReplaced < 0 ? remaining.length
      : current.slice(0, firstReplaced).filter((line) => remaining.some((item) => item.budgetLineId === line.budgetLineId)).length;
    setLines([...remaining.slice(0, insertion), ...patched, ...remaining.slice(insertion)]);
    setManualLineIds((previous) => new Set([...previous].filter((id) => !ids.has(id))));
    setRecentLineIds(patched.map((line) => line.budgetLineId));
    const claimedIds = new Set(patched.flatMap(line => line.replacesLineIds ?? []));
    const deleted = deletedLinesByCategoryRef.current.bus;
    if (deleted?.some(line => claimedIds.has(line.budgetLineId))) {
      const retained = deleted.filter(line => !claimedIds.has(line.budgetLineId));
      const next = { ...deletedLinesByCategoryRef.current };
      if (retained.length) next.bus = retained;
      else delete next.bus;
      replaceDeletedLines(next);
    }
    return true;
  }, [baselineLines, budgetOrderId, calculateTax, draft, lines, manualLineIds, replaceDeletedLines]);

  /** 删除明确范围并保留局部撤销，确认框打开后状态变化不能越权。 */
  const removeLines = useCallback((lineIds: string[]) => {
    if (!localMutationAllowedRef.current || workingContextRef.current.budgetOrderId !== budgetOrderId) return;
    const ids = new Set(lineIds);
    const current = buildEffectiveLines(lines, draft, manualLineIds, calculateTax);
    const removed = current.filter((line) => ids.has(line.budgetLineId));
    if (!removed.length || removed.some((line) => line.isLocked || isBusinessTaxLine(line))) return;
    recordDeletedLines(removed);
    setLines(lines.filter((line) => !ids.has(line.budgetLineId)));
    setRecentLineIds([]);
  }, [budgetOrderId, calculateTax, draft, lines, manualLineIds, recordDeletedLines]);

  /** 本机恢复保持服务端基线，只有同一版本的可编辑同revision工作态可采用。 */
  const restoreLocalDraft = useCallback((revision: string, restoredDraft: QuoteBudgetEditorDraft, restoredLines: QuoteBudgetLine[]) => {
    if (!localMutationAllowedRef.current || !currentEditor || revision !== currentEditor.revision
      || revision !== baselineRevision || currentEditor.budgetOrder.budgetOrderId !== budgetOrderId) return false;
    if ([restoredDraft.studentCount, restoredDraft.teacherCount, restoredDraft.guideCount, restoredDraft.driverCount]
      .some((value) => !Number.isInteger(value) || value < 0 || value > 10000)) return false;
    setDraft(restoredDraft);
    setLines(restoredLines);
    setManualLineIds(new Set());
    replaceDeletedLines({});
    return true;
  }, [baselineRevision, budgetOrderId, currentEditor, replaceDeletedLines]);

  // 人数→金额联动（原型 rowAmount 口径）：人数联动行随人数草稿实时重算数量与金额，
  // 供明细面板渲染、汇总与保存共用；普通手改行与非联动行保持原值.
  const effectiveLines = useMemo(() => {
    return buildEffectiveLines(lines, draft, manualLineIds, calculateTax);
  }, [calculateTax, lines, draft, manualLineIds]);

  const mutation = useMutation({
    mutationFn: async ({ context, request }: SaveMutationInput) => {
      const currentQueryKey = editorGenerationQueryKey(
        context.budgetOrderId,
        context.generation,
      );
      await queryClient.cancelQueries({ queryKey: currentQueryKey, exact: true });
      try {
        return await saveBudgetDraft(context.budgetOrderId, request);
      } finally {
        await queryClient.cancelQueries({ queryKey: currentQueryKey, exact: true });
      }
    },
    onSuccess: (editor, { context, manualLines }) => {
      const nextManualLineIds = equivalentManualLineIds(editor.lines, manualLines);
      if (applyAuthoritativeEditor(editor, context, nextManualLineIds)) {
        setSaveConflictContext(null);
      }
    },
    onError: (error, { context }) => {
      if (isConcurrentUpdateError(error) && matchesActiveContext(context)) {
        setSaveConflictContext(context);
        return;
      }
      return refreshAfterCustomerQuoteLock(error, context);
    },
  });

  const conflictReloadMutation = useMutation({
    mutationFn: async ({ context }: { context: EditorRequestContext; commit?: (apply: () => boolean) => Promise<boolean> }) => {
      const currentQueryKey = editorGenerationQueryKey(
        context.budgetOrderId,
        context.generation,
      );
      await queryClient.cancelQueries({ queryKey: currentQueryKey, exact: true });
      try {
        return await fetchBudgetEditor(context.budgetOrderId);
      } finally {
        await queryClient.cancelQueries({ queryKey: currentQueryKey, exact: true });
      }
    },
    onSuccess: async (editor, { context, commit }) => {
      const apply = () => {
        if (!mountedRef.current || !matchesActiveContext(context)) return false;
        if (!applyAuthoritativeEditor(editor, context)) return false;
        setSaveConflictContext(null);
        setConflictReloadFailure(null);
        mutation.reset();
        return true;
      };
      if (!mountedRef.current || !matchesActiveContext(context)) throw new EditorContextChangedError();
      if (commit ? !await commit(apply) : !apply()) throw new Error("本地草稿或页面已变化，未采用重载数据，请核对后重试。");
    },
    onError: (error, { context }) => {
      if (!mountedRef.current || !matchesActiveContext(context)) return;
      setConflictReloadFailure({
        context,
        error: error instanceof Error ? error : new Error("重新加载最新数据失败"),
      });
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: ({ context, confirmRiskCodes, versionName }: FinalizeMutationInput) =>
      finalizeBudget(context.budgetOrderId, { confirmRiskCodes, versionName }),
    onSuccess: (result, { context, versionName }) => {
      const current = contextRef.current;
      if (
        current !== null
        && current.budgetOrderId === context.budgetOrderId
        && current.generation === context.generation
      ) {
        setBaselineDraft(draft);
        setBaselineLines(lines);
      }
      queryClient.setQueryData<QuoteBudgetEditorData>(
        editorGenerationQueryKey(context.budgetOrderId, context.generation),
        (current) => current
          ? {
              ...current,
              budgetOrder: {
                ...current.budgetOrder,
                status: result.status,
                costBudgetTotalAmount: result.costBudgetTotalAmount,
                ...(versionName ? { versionName } : {}),
              },
              versions: current.versions.map((version) =>
                version.budgetOrderId === result.budgetOrderId
                  ? {
                      ...version,
                      status: result.status,
                      costBudgetTotalAmount: result.costBudgetTotalAmount,
                    }
                  : version,
              ),
              readonly: true,
              readonlyReason: "finalized",
            }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: editorQueryKey(context.budgetOrderId) });
    },
    onError: (error, { context }) =>
      refreshAfterCustomerQuoteLock(error, context),
  });
  useEffect(() => {
    if (isDirty && finalizeMutation.error && !isItineraryChangedFinalizeError(finalizeMutation.error)) {
      finalizeMutation.reset();
    }
  }, [finalizeMutation, isDirty]);

  const regenerateV1Mutation = useMutation({
    mutationFn: async (context: EditorRequestContext) => {
      if (!matchesActiveContext(context)) throw new EditorContextChangedError();
      return regenerateV1FromItinerary(context.budgetOrderId);
    },
    onSuccess: (editor, context) => {
      applyAuthoritativeEditor(editor, context);
    },
  });

  const rebuildFromItineraryMutation = useMutation({
    mutationFn: async ({
      context,
      reuseExactMatchedPrices,
    }: {
      context: EditorRequestContext;
      reuseExactMatchedPrices: boolean;
    }) => {
      if (!matchesActiveContext(context)) throw new EditorContextChangedError();
      const previousRequest = rebuildRequestRef.current;
      const request = previousRequest
        && previousRequest.context.budgetOrderId === context.budgetOrderId
        && previousRequest.context.generation === context.generation
        ? previousRequest
        : {
            context,
            idempotencyKey: createRebuildIdempotencyKey(),
            reuseExactMatchedPrices,
        };
      rebuildRequestRef.current = request;
      let result = rebuiltVersionRef.current;
      if (result === null) {
        try {
          result = await createBudgetVersionFromItinerary(context.budgetOrderId, {
            idempotencyKey: request.idempotencyKey,
            reuseExactMatchedPrices: request.reuseExactMatchedPrices,
          });
        } catch (error) {
          if (!matchesActiveContext(context)) throw error;
          rebuildSourceReadonlyRef.current = true;
          try {
            const refreshed = await fetchBudgetEditor(context.budgetOrderId);
            if (matchesActiveContext(context)) {
              queryClient.setQueryData(
                editorGenerationQueryKey(context.budgetOrderId, context.generation),
                refreshed,
              );
              rebuildSourceReadonlyRef.current = refreshed.readonly;
            }
          } catch {
            // 创建结果和来源状态均未知时保守只读，避免在写入结果不明时继续修改来源版本。
          }
          throw error;
        }
      }
      if (!matchesActiveContext(context)) throw new EditorContextChangedError();
      rebuiltVersionRef.current = result;
      try {
        if (!matchesActiveContext(context)) throw new EditorContextChangedError();
        await preloadVersion(result.budgetOrderId);
        if (!matchesActiveContext(context)) throw new EditorContextChangedError();
      } catch (error) {
        if (!matchesActiveContext(context)) throw new EditorContextChangedError();
        throw new RebuildVersionPreloadError(result, error);
      }
      return result;
    },
  });

  const priceReusePreviewMutation = useMutation({
    mutationFn: async (context: EditorRequestContext) => ({
      context,
      preview: await fetchRebuildPriceReusePreview(context.budgetOrderId),
    }),
  });
  const resetPriceReusePreviewRef = useRef(priceReusePreviewMutation.reset);
  resetPriceReusePreviewRef.current = priceReusePreviewMutation.reset;
  useEffect(() => {
    resetPriceReusePreviewRef.current();
  }, [budgetOrderId, contextGeneration]);

  const unfinalizeMutation = useMutation({
    mutationFn: (requestContext: EditorRequestContext) =>
      unfinalizeBudget(requestContext.budgetOrderId),
    onSuccess: async (result, requestContext) => {
      const current = contextRef.current;
      if (
        current !== null
        && current.budgetOrderId === requestContext.budgetOrderId
        && current.generation === requestContext.generation
      ) {
        setBaselineDraft(draft);
        setBaselineLines(lines);
      }
      queryClient.setQueryData<QuoteBudgetEditorData>(
        editorGenerationQueryKey(requestContext.budgetOrderId, requestContext.generation),
        (editor) => editor
          ? {
              ...editor,
              budgetOrder: {
                ...editor.budgetOrder,
                status: result.status,
                costBudgetTotalAmount: result.costBudgetTotalAmount,
              },
              versions: editor.versions.map((version) =>
                version.budgetOrderId === result.budgetOrderId
                  ? {
                      ...version,
                      status: result.status,
                      costBudgetTotalAmount: result.costBudgetTotalAmount,
                    }
                  : version,
              ),
              readonly: false,
              readonlyReason: null,
            }
          : editor,
      );
      await queryClient.invalidateQueries({
        queryKey: editorQueryKey(requestContext.budgetOrderId),
      });
    },
    onError: (error, requestContext) =>
      refreshAfterCustomerQuoteLock(error, requestContext),
  });
  const activeUnfinalizeRef = useRef<{
    context: EditorRequestContext;
    promise: ReturnType<typeof unfinalizeMutation.mutateAsync>;
  } | null>(null);

  const requestUnfinalize = useCallback(() => {
    if (!canMutateRef.current) {
      return Promise.reject(new Error("当前账号已无修改行程核价的权限"));
    }
    const context = { budgetOrderId, generation: contextGeneration };
    const active = activeUnfinalizeRef.current;
    if (
      active
      && active.context.budgetOrderId === context.budgetOrderId
      && active.context.generation === context.generation
    ) {
      return active.promise;
    }
    const promise = unfinalizeMutation.mutateAsync(context);
    activeUnfinalizeRef.current = { context, promise };
    const clear = () => {
      if (activeUnfinalizeRef.current?.promise === promise) {
        activeUnfinalizeRef.current = null;
      }
    };
    void promise.then(clear, clear);
    return promise;
  }, [budgetOrderId, contextGeneration, unfinalizeMutation]);

  const summary = useMemo(() => {
    if (!currentEditor || !draft) {
      return null;
    }
    return calculateBudgetSummary(
      {
        studentCount: draft.studentCount,
        teacherCount: draft.teacherCount,
        teacherPays: draft.teacherPays,
        targetMarginRate: currentEditor.budgetOrder.targetMarginRate,
      },
      effectiveLines,
      { savedAmounts: true },
    );
  }, [currentEditor, draft, effectiveLines]);

  const costBreakdown = useMemo(() => {
    if (!currentEditor || !draft) return undefined;
    return calculateCostBreakdown(
      draft,
      effectiveLines,
      currentEditor.costBreakdown?.auditIssues,
      true,
    );
  }, [currentEditor, draft, effectiveLines, isDirty]);

  const isCurrentContext = (requestContext: EditorRequestContext | undefined) =>
    Boolean(
      requestContext
      && requestContext.budgetOrderId === budgetOrderId
      && requestContext.generation === contextGeneration,
    );
  const currentSaveError = isCurrentContext(mutation.variables?.context) ? mutation.error : null;
  const currentSaving = mutation.isPending && isCurrentContext(mutation.variables?.context);
  const currentSaveConflict = isCurrentContext(saveConflictContext ?? undefined);
  const currentConflictReloadRequest = isCurrentContext(conflictReloadMutation.variables?.context);
  const currentFinalizeError = isCurrentContext(finalizeMutation.variables?.context)
    ? finalizeMutation.error
    : null;
  const currentUnfinalizeError = isCurrentContext(unfinalizeMutation.variables)
    ? unfinalizeMutation.error
    : null;
  const currentLockRefreshError = isCurrentContext(lockRefreshFailure?.context)
    ? lockRefreshFailure?.error ?? null
    : null;
  const currentRebuildRequest = isCurrentContext(
    rebuildFromItineraryMutation.variables?.context,
  );
  const currentPreviewRequest = isCurrentContext(
    priceReusePreviewMutation.variables ?? priceReusePreviewContextRef.current ?? undefined,
  );
  const currentPriceReusePreview = isCurrentContext(priceReusePreviewMutation.data?.context)
    ? priceReusePreviewMutation.data?.preview
    : undefined;
  const loadError = currentEditor ? null : query.error;
  const refreshError = currentEditor ? query.error : null;
  localMutationAllowedRef.current = canMutateRef.current && Boolean(currentEditor)
    && currentEditor?.budgetOrder.status === "draft" && !currentEditor?.readonly
    && !currentSaving && !currentSaveConflict && !finalizeMutation.isPending
    && !unfinalizeMutation.isPending && !rebuildFromItineraryMutation.isPending
    && !regenerateV1Mutation.isPending
    && !(isCustomerQuoteLockError(currentSaveError) || isRosterPricingLockError(currentSaveError)) && !(isCustomerQuoteLockError(currentFinalizeError) || isRosterPricingLockError(currentFinalizeError))
    && !(isCustomerQuoteLockError(currentUnfinalizeError) || isRosterPricingLockError(currentUnfinalizeError)) && rebuiltVersionRef.current === null;

  return {
    editor: currentEditor,
    isLoading: !loadError && !editorGenerationReady,
    reloading: query.isFetching,
    error: loadError,
    refreshError,
    reload: () => query.refetch(),
    readonly:
      !canMutate
      || currentEditor === null
      || currentEditor.readonly
      || (isCustomerQuoteLockError(currentSaveError) || isRosterPricingLockError(currentSaveError))
      || (isCustomerQuoteLockError(currentFinalizeError) || isRosterPricingLockError(currentFinalizeError))
      || (isCustomerQuoteLockError(currentUnfinalizeError) || isRosterPricingLockError(currentUnfinalizeError))
      || rebuiltVersionRef.current !== null
      || rebuildSourceReadonlyRef.current,
    draft: currentEditor ? draft : null,
    lines: currentEditor ? effectiveLines : [],
    workingRevision: baselineRevision,
    recentLineId: currentEditor ? recentLineIds.at(-1) ?? null : null,
    recentLineIds: currentEditor ? recentLineIds : [],
    contextGeneration,
    preloadVersion,
    searchTransportPoints,
    applyLineFormula,
    removeLine,
    removeResourceType,
    undoLastDeletion,
    deletedLineCountByCategory: Object.fromEntries(
      Object.entries(deletedLinesByCategory).map(([category, removed]) => [category, removed.length]),
    ) as Partial<Record<WorkspaceCategory, number>>,
    addResolvedEntries,
    commitManualLines,
    removeLines,
    restoreLocalDraft,
    isDirty,
    summary,
    costBreakdown,
    saving: currentSaving,
    saveError: currentSaveError,
    saveConflict: currentSaveConflict,
    reloadingAfterConflict:
      conflictReloadMutation.isPending && currentConflictReloadRequest,
    conflictReloadError:
      isCurrentContext(conflictReloadFailure?.context)
        ? conflictReloadFailure?.error ?? null
        : null,
    reloadLatestAfterConflict: (commit?: (apply: () => boolean) => Promise<boolean>) => {
      if (!currentSaveConflict && !commit) {
        return Promise.reject(new Error("当前不存在待处理的核价并发冲突"));
      }
      setConflictReloadFailure(null);
      return conflictReloadMutation.mutateAsync({ context: { budgetOrderId, generation: contextGeneration }, commit });
    },
    lockRefreshError: currentLockRefreshError,
    finalizing:
      finalizeMutation.isPending && isCurrentContext(finalizeMutation.variables?.context),
    finalizeError: currentFinalizeError,
    finalize: (confirmRiskCodes: string[] = [], versionName?: string) => {
      if (!canMutateRef.current || currentSaveConflict || currentSaving || isDirty) return;
      finalizeMutation.mutate({
        context: { budgetOrderId, generation: contextGeneration },
        confirmRiskCodes,
        versionName,
      });
    },
    regeneratingV1:
      regenerateV1Mutation.isPending && isCurrentContext(regenerateV1Mutation.variables),
    regenerateV1Error: isCurrentContext(regenerateV1Mutation.variables)
      ? regenerateV1Mutation.error
      : null,
    regenerateV1: () => {
      if (!canMutateRef.current) return Promise.reject(new Error("当前入口仅可查看核价"));
      if (currentSaveConflict) {
        return Promise.reject(new Error("核价已被其他用户更新，请先重新加载最新数据"));
      }
      return regenerateV1Mutation.mutateAsync({
        budgetOrderId,
        generation: contextGeneration,
      });
    },
    rebuildingFromItinerary: rebuildFromItineraryMutation.isPending && currentRebuildRequest,
    rebuildFromItineraryError: currentRebuildRequest
      ? rebuildFromItineraryMutation.error
      : null,
    hasCreatedRebuildVersion: rebuiltVersionRef.current !== null,
    hasStartedRebuildRequest: rebuildRequestRef.current !== null,
    rebuildReuseExactMatchedPrices:
      rebuildRequestRef.current?.reuseExactMatchedPrices ?? null,
    rebuildFromItinerary: (reuseExactMatchedPrices = false) => {
      if (!canMutateRef.current) return Promise.reject(new Error("当前入口仅可查看核价"));
      if (currentSaveConflict) {
        return Promise.reject(new Error("核价已被其他用户更新，请先重新加载最新数据"));
      }
      return rebuildFromItineraryMutation.mutateAsync({
        context: { budgetOrderId, generation: contextGeneration },
        reuseExactMatchedPrices,
      });
    },
    priceReusePreview: currentPriceReusePreview,
    loadingPriceReusePreview: priceReusePreviewMutation.isPending && currentPreviewRequest,
    priceReusePreviewError: currentPreviewRequest ? priceReusePreviewMutation.error : null,
    loadPriceReusePreview: () => {
      const context = { budgetOrderId, generation: contextGeneration };
      priceReusePreviewContextRef.current = context;
      priceReusePreviewMutation.reset();
      return priceReusePreviewMutation.mutateAsync(context).then((result) => result.preview);
    },
    unfinalizing:
      unfinalizeMutation.isPending && isCurrentContext(unfinalizeMutation.variables),
    unfinalizeError: currentUnfinalizeError,
    unfinalize: requestUnfinalize,
    setStudentCount: (value: number) => patchDraft({ studentCount: value }),
    setTeacherCount: (value: number) => patchDraft({ teacherCount: value }),
    setGuideCount: (value: number) => patchDraft({ guideCount: value }),
    setDriverCount: (value: number) => patchDraft({ driverCount: value }),
    setTeacherPays: (value: boolean) => patchDraft({ teacherPays: value }),
    save: () => {
      if (!canMutateRef.current) return Promise.reject(new Error("当前入口仅可查看核价"));
      const context = { budgetOrderId, generation: contextGeneration };
      if (currentSaveConflict) {
        return Promise.reject(new Error("核价已被其他用户更新，请先重新加载最新数据"));
      }
      if (!currentEditor || !draft || !baselineRevision) {
        return Promise.reject(new Error("编辑器尚未就绪，无法保存"));
      }
      const order = currentEditor.budgetOrder;
      const request: SaveDraftRequest = {
        expectedRevision: baselineRevision,
        versionName: order.versionName,
        studentCount: draft.studentCount,
        teacherCount: draft.teacherCount,
        guideCount: draft.guideCount,
        driverCount: draft.driverCount,
        teacherPays: draft.teacherPays,
        targetMarginRate: order.targetMarginRate,
        remark: order.remark ?? "",
        lines: effectiveLines.map((line) => toDraftLine(line)),
      };
      return mutation.mutateAsync({
        context,
        request,
        manualLines: effectiveLines.filter((line) => manualLineIds.has(line.budgetLineId)),
      });
    },
  };
}
